import { useEffect, useState } from "react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { RecentActivityTable } from "@/components/RecentActivityTable";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { StatCardSkeleton, ActivityTableSkeleton } from "@/components/skeletons/DashboardSkeletons";
import { Mail, Star, Users, TrendingUp, Plus, Zap, AlertCircle, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { Staff, Interaction, Business } from "@shared/schema";
import { format } from "date-fns";
import { getPlanLimits, getPlanDisplayName } from "@shared/plans";
import { OnboardingModal } from "@/components/OnboardingModal";

// Analytics components
import {
  PeriodSelector,
  StatCardWithComparison,
  TrendChart,
  FunnelView,
  SourceComparison,
  AnalyticsUpgrade,
  ExportButton,
  type AnalyticsPeriod,
} from "@/components/analytics";

// Types for the enhanced stats response
interface PeriodStats {
  requestsSent: number;
  reviewsReceived: number;
  avgRating: number;
  responseRate: number;
}

interface EnhancedBusinessStats {
  period: AnalyticsPeriod;
  current: PeriodStats;
  previous: PeriodStats | null;
  trend: {
    granularity: "day" | "week" | "month";
    data: Array<{ date: string; requests: number; reviews: number }>;
  };
  funnel: {
    sent: number;
    visited: number;
    reviewed: number;
  };
  bySource: {
    email: { count: number; avgRating: number; responseRate: number };
    qr: { count: number; avgRating: number };
  };
}

export default function Dashboard() {
  const { toast } = useToast();
  const [period, setPeriod] = useState<AnalyticsPeriod>("30d");
  
  const { data: business } = useQuery<Business>({
    queryKey: ["/api/business"],
  });
  
  const { data: stats, isLoading: statsLoading, isError: statsError, refetch: refetchStats } = useQuery<EnhancedBusinessStats>({
    queryKey: [`/api/business/stats?period=${period}`],
  });
  
  // Calculate usage data
  const planType = business?.planType || "free";
  const planLimits = getPlanLimits(planType);
  const monthlyUsed = business?.monthlyRequestCount || 0;
  const monthlyLimit = planLimits.maxRequestsPerMonth;
  const isUnlimited = monthlyLimit === Infinity;
  const usagePercent = isUnlimited ? 0 : Math.min(100, (monthlyUsed / monthlyLimit) * 100);
  const remaining = isUnlimited ? Infinity : Math.max(0, monthlyLimit - monthlyUsed);
  const isApproachingLimit = !isUnlimited && remaining <= 3 && remaining > 0;
  const isAtLimit = remaining === 0;
  const isFreeUser = planType === "free";

  // Handle AppSumo activation success/failure messages
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const appsumoStatus = params.get("appsumo");
    
    if (appsumoStatus === "activated") {
      toast({
        title: "🎉 Welcome, Sumo-ling!",
        description: "Your AppSumo license has been activated successfully.",
      });
      // Clean up URL
      window.history.replaceState({}, "", "/dashboard");
    } else if (appsumoStatus === "link_failed") {
      toast({
        title: "Account Created",
        description: "Your account was created, but we couldn't link your AppSumo license. Please contact support.",
        variant: "destructive",
      });
      window.history.replaceState({}, "", "/dashboard");
    }
  }, [toast]);

  const { data: staffList } = useQuery<Staff[]>({
    queryKey: ["/api/staff"],
  });

  const { data: interactionsList, isLoading: interactionsLoading } = useQuery<Interaction[]>({
    queryKey: ["/api/interactions"],
  });

  // Build staff lookup map
  const staffMap = new Map<string, string>();
  staffList?.forEach((s) => {
    staffMap.set(s.id, s.name);
  });

  // Transform interactions to activity format
  const activities = (interactionsList || []).map((interaction) => ({
    id: interaction.id,
    customerName: "Customer", // We don't store customer name in interactions
    staffName: interaction.staffId ? staffMap.get(interaction.staffId) || "Unknown" : "Unknown",
    source: interaction.source as "email" | "qr",
    rating: interaction.rating || undefined,
    status: (interaction.rating
      ? (interaction.rating === 5 ? "reviewed" : "feedback")
      : "clicked") as "reviewed" | "feedback" | "clicked",
    date: interaction.createdAt ? format(new Date(interaction.createdAt), "MMM d, yyyy") : "-",
  }));

  // Check if user needs onboarding
  const showOnboarding = business && !business.onboardingCompleted;

  return (
    <>
      {/* Onboarding Modal */}
      {showOnboarding && (
        <OnboardingModal isOpen={true} businessId={business.id} />
      )}

      {/* Main Dashboard */}
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header with Period Selector */}
      <div className="flex flex-col gap-4 mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold">Dashboard</h1>
            <p className="text-muted-foreground mt-1">
              Welcome back! Here's your review collection overview.
            </p>
          </div>
          <Link href="/dashboard/send">
            <Button data-testid="button-send-request">
              <Plus className="h-4 w-4 mr-2" />
              Send Request
            </Button>
          </Link>
        </div>
        
        {/* Period Selector and Export - Show for all users */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          {!isFreeUser && <PeriodSelector value={period} onChange={setPeriod} />}
          <ExportButton type="dashboard" period={period} isFreeUser={isFreeUser} />
        </div>
      </div>

      {/* Stats Cards with Comparison */}
      {statsError ? (
        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <AlertCircle className="h-12 w-12 text-destructive mb-4" />
              <h3 className="text-lg font-semibold mb-2">Failed to Load Analytics</h3>
              <p className="text-muted-foreground mb-4 max-w-md">
                We couldn't load your analytics data. This might be a temporary network issue.
              </p>
              <Button onClick={() => refetchStats()} variant="outline">
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {statsLoading ? (
              <>
                <StatCardSkeleton />
                <StatCardSkeleton />
                <StatCardSkeleton />
              </>
            ) : (
              <>
                <StatCardWithComparison
                  title="Requests Sent"
                  value={stats?.current.requestsSent || 0}
                  previousValue={stats?.previous?.requestsSent}
                  icon={<Mail className="h-6 w-6" />}
                  format="number"
                />
                <StatCardWithComparison
                  title="Response Rate"
                  value={stats?.current.responseRate || 0}
                  previousValue={stats?.previous?.responseRate}
                  icon={<TrendingUp className="h-6 w-6" />}
                  format="percentage"
                />
                <StatCardWithComparison
                  title="Avg Rating"
                  value={stats?.current.avgRating || 0}
                  previousValue={stats?.previous?.avgRating}
                  icon={<Star className="h-6 w-6" />}
                  format="rating"
                />
              </>
            )}
          </div>

          {/* Advanced Analytics - Show upgrade prompt for free users */}
          {isFreeUser ? (
            <div className="mb-8">
              <AnalyticsUpgrade variant="compact" />
            </div>
          ) : (
            <>
              {/* Performance Trends Chart */}
              <div className="mb-8">
                <TrendChart
                  data={stats?.trend.data || []}
                  isLoading={statsLoading}
                  granularity={stats?.trend.granularity}
                />
              </div>

              {/* Funnel and Source Comparison */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                <FunnelView
                  funnel={stats?.funnel || { sent: 0, visited: 0, reviewed: 0 }}
                  isLoading={statsLoading}
                />
                <SourceComparison
                  sources={stats?.bySource || {
                    email: { count: 0, avgRating: 0, responseRate: 0 },
                    qr: { count: 0, avgRating: 0 },
                  }}
                  isLoading={statsLoading}
                />
              </div>
            </>
          )}
        </>
      )}

      {/* Usage Indicator */}
      <Card className={`mb-8 ${isAtLimit ? "border-destructive" : isApproachingLimit ? "border-yellow-500" : ""}`}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              <CardTitle className="text-lg">Monthly Usage</CardTitle>
            </div>
            <Badge variant={planType === "free" ? "outline" : "default"}>
              {getPlanDisplayName(planType)}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Review requests this month</span>
              <span className="font-medium">
                {monthlyUsed} / {isUnlimited ? "Unlimited" : monthlyLimit}
              </span>
            </div>
            {!isUnlimited && (
              <Progress 
                value={usagePercent} 
                className={`h-2 ${isAtLimit ? "[&>div]:bg-destructive" : isApproachingLimit ? "[&>div]:bg-yellow-500" : ""}`}
              />
            )}
            {isAtLimit && planType === "free" && (
              <p className="text-sm text-destructive">
                You've reached your monthly limit. 
                <Link href="/pricing" className="ml-1 underline font-medium">
                  Upgrade now
                </Link>
              </p>
            )}
            {isAtLimit && planType === "lifetime" && (
              <p className="text-sm text-destructive">
                You've reached your 500 request limit. 
                <Link href="/pricing" className="ml-1 underline font-medium">
                  Upgrade to Pro for unlimited
                </Link>
              </p>
            )}
            {isApproachingLimit && planType === "free" && (
              <p className="text-sm text-yellow-600">
                Only {remaining} request{remaining !== 1 ? "s" : ""} remaining. 
                <Link href="/pricing" className="ml-1 underline font-medium">
                  Upgrade for more
                </Link>
              </p>
            )}
            {isApproachingLimit && planType === "lifetime" && (
              <p className="text-sm text-yellow-600">
                Only {remaining} request{remaining !== 1 ? "s" : ""} remaining this month.
              </p>
            )}
            {planType === "free" && !isAtLimit && !isApproachingLimit && (
              <p className="text-sm text-muted-foreground">
                <Link href="/pricing" className="text-primary hover:underline">
                  Upgrade to Lifetime
                </Link>
                {" "}for 500 requests/month and unlimited staff.
              </p>
            )}
            {planType === "lifetime" && !isAtLimit && !isApproachingLimit && (
              <p className="text-sm text-muted-foreground">
                Need unlimited requests?{" "}
                <Link href="/pricing" className="text-primary hover:underline">
                  Upgrade to Pro
                </Link>
              </p>
            )}
            {planType === "pro" && (
              <p className="text-sm text-green-600">
                You have unlimited review requests with your Pro plan.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4 pb-4">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          {interactionsLoading ? (
            <ActivityTableSkeleton rows={5} />
          ) : activities.length > 0 ? (
            <RecentActivityTable activities={activities} />
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No activity yet. Send your first review request to get started!
            </div>
          )}
        </CardContent>
      </Card>
    </div>
    </>
  );
}
